import { useEffect, useId, useState } from 'react';
import { type AnalyticsFilters } from '../../services/analytics';
import { AccountTableCard } from './AccountTableCard';
import { formatInt } from './format';
import {
    DEFAULT_INACTIVITY_DAYS,
    INACTIVE_COLUMNS,
    INACTIVE_FORMATTERS,
    inactivitySummary,
    parseDays,
    type InactivityKind,
} from './inactivity';
import styles from './InactiveAccounts.module.css';

interface InactiveAccountsProps {
    filters: AnalyticsFilters;
    enabled: boolean;
    refreshKey: number;
    timeZone: string;
}

const DEBOUNCE_MS = 400;

/** Espera a pessoa parar de digitar antes de consultar. */
function useDebounced(value: string, ms: number): string {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const id = setTimeout(() => setDebounced(value), ms);
        return () => clearTimeout(id);
    }, [value, ms]);
    return debounced;
}

export function InactiveAccounts({ filters, enabled, refreshKey, timeZone }: InactiveAccountsProps) {
    const loginId = useId();
    const lancId = useId();
    const [kind, setKind] = useState<InactivityKind>('login');
    const [loginDays, setLoginDays] = useState(String(DEFAULT_INACTIVITY_DAYS));
    const [lancDays, setLancDays] = useState(String(DEFAULT_INACTIVITY_DAYS));
    // Valor aplicado: so muda quando o texto e valido, entao um campo vazio nao dispara consulta.
    const [applied, setApplied] = useState({ login: DEFAULT_INACTIVITY_DAYS, lancamento: DEFAULT_INACTIVITY_DAYS });

    const debouncedLogin = useDebounced(loginDays, DEBOUNCE_MS);
    const debouncedLanc = useDebounced(lancDays, DEBOUNCE_MS);

    useEffect(() => {
        const login = parseDays(debouncedLogin);
        const lancamento = parseDays(debouncedLanc);
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setApplied((a) => ({ login: login ?? a.login, lancamento: lancamento ?? a.lancamento }));
    }, [debouncedLogin, debouncedLanc]);

    const days = applied[kind];
    const invalid = (raw: string) => raw !== '' && parseDays(raw) === null;

    return (
        <AccountTableCard
            name="sem-atividade"
            title="Contas sem atividade"
            subtitle="Informe o mínimo de dias corridos. A lista respeita os filtros globais e usa a data final do período."
            params={{ kind, days: String(days) }}
            renderHeader={(table) => (
                <div className={styles.header}>
                    <div className={styles.inputs}>
                        <div className={styles.field}>
                            <label htmlFor={loginId} className={styles.label}>
                                Dias sem acesso
                            </label>
                            <input
                                id={loginId}
                                type="number"
                                inputMode="numeric"
                                min={1}
                                max={365}
                                className={`${styles.input} ${invalid(loginDays) ? styles.invalid : ''}`}
                                value={loginDays}
                                onChange={(e) => setLoginDays(e.target.value)}
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor={lancId} className={styles.label}>
                                Dias sem lançamento
                            </label>
                            <input
                                id={lancId}
                                type="number"
                                inputMode="numeric"
                                min={1}
                                max={365}
                                className={`${styles.input} ${invalid(lancDays) ? styles.invalid : ''}`}
                                value={lancDays}
                                onChange={(e) => setLancDays(e.target.value)}
                            />
                        </div>
                        <div className={styles.toggle} role="group" aria-label="Tipo de inatividade">
                            <button
                                type="button"
                                className={`${styles.option} ${kind === 'login' ? styles.optionActive : ''}`}
                                aria-pressed={kind === 'login'}
                                onClick={() => setKind('login')}
                            >
                                Sem acesso
                            </button>
                            <button
                                type="button"
                                className={`${styles.option} ${kind === 'lancamento' ? styles.optionActive : ''}`}
                                aria-pressed={kind === 'lancamento'}
                                onClick={() => setKind('lancamento')}
                            >
                                Sem lançamento
                            </button>
                        </div>
                    </div>
                    {(invalid(loginDays) || invalid(lancDays)) && (
                        <p className={styles.hint}>Use um número inteiro de 1 a 365.</p>
                    )}
                    {table && (
                        <p className={styles.summary}>
                            <strong>{formatInt(table.rows.length)}</strong>{' '}
                            {inactivitySummary(table.rows.length, days, kind).replace(/^\S+\s/, '')}
                        </p>
                    )}
                </div>
            )}
            columns={INACTIVE_COLUMNS}
            formatters={INACTIVE_FORMATTERS}
            exportFilename="analytics-contas-sem-atividade.xlsx"
            emptyMessage="Nenhuma conta com esse tempo sem atividade."
            filters={filters}
            enabled={enabled}
            refreshKey={refreshKey}
            timeZone={timeZone}
        />
    );
}
